import { useRef, useState } from 'react';
import { Upload, Camera, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import { compressIfImage } from '@/lib/imageCompression';
import type { FileKind } from '@/types';

interface FileUploadProps {
  familyId: string;
  itemId: string;
  createdBy: string;
  kind?: FileKind;
  onUploaded?: () => void;
}

export function FileUpload({ familyId, itemId, createdBy, kind = 'other', onUploaded }: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let anySucceeded = false;

    for (const raw of Array.from(files)) {
      try {
        // Resolve MIME type — browsers sometimes report '' for PDFs on iOS
        const ext = raw.name.split('.').pop()?.toLowerCase() ?? '';
        const resolvedMime = raw.type || (ext === 'pdf' ? 'application/pdf' : 'application/octet-stream');

        const file = await compressIfImage(raw);
        const finalMime = file.type || resolvedMime;
        const storagePath = `${familyId}/${itemId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext || 'bin'}`;

        const { error: uploadErr } = await supabase.storage
          .from('invoices')
          .upload(storagePath, file, { contentType: finalMime, upsert: false });

        if (uploadErr) {
          console.error('Storage upload error:', uploadErr);
          toast.error(`Upload failed: ${uploadErr.message}`);
          continue;
        }

        const { error: dbErr } = await supabase.from('files').insert({
          item_id: itemId,
          family_id: familyId,
          kind,
          storage_path: storagePath,
          file_name: raw.name,
          mime: finalMime,
          created_by: createdBy
        });

        if (dbErr) {
          console.error('DB insert error:', dbErr);
          // Storage upload succeeded but DB failed — clean up the orphan
          await supabase.storage.from('invoices').remove([storagePath]);
          toast.error(`Save failed: ${dbErr.message}`);
          continue;
        }

        anySucceeded = true;
        toast.success(`${raw.name} uploaded`);
      } catch (e) {
        console.error('FileUpload exception:', e);
        toast.error(`Error: ${e instanceof Error ? e.message : 'Unknown error'}`);
      }
    }

    setUploading(false);
    if (anySucceeded) onUploaded?.();

    // Reset inputs so the same file can be selected again
    if (inputRef.current) inputRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  };

  return (
    <div className="space-y-2">
      {/* Hidden inputs — one for gallery/files, one for camera */}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {uploading ? (
        <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-brand-300 rounded-xl text-brand-600 text-sm bg-brand-50">
          <Loader2 className="w-4 h-4 animate-spin" />
          Uploading…
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex items-center justify-center gap-2 px-3 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 text-sm hover:border-brand-400 hover:text-brand-600 transition-colors"
          >
            <Upload className="w-4 h-4" />
            From files
          </button>
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            className="flex items-center justify-center gap-2 px-3 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 text-sm hover:border-brand-400 hover:text-brand-600 transition-colors"
          >
            <Camera className="w-4 h-4" />
            Take photo
          </button>
        </div>
      )}
    </div>
  );
}
